import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useIntl, defineMessages } from 'react-intl'
import {
  useWishlist,
  useNavigation,
  useCookie,
  getEventItemFromProduct
} from 'msp-integrations'
import IconButton from '@molecules/IconButton/IconButton'
import { Button } from '@atoms/Button'
import { WishlistButtonProps } from './WishlistButton.types'
import Spinner from '@atoms/Spinner/Spinner'

const WishlistButton = ({
  product,
  isWishlistPage = false,
  isDisabled = false,
  wishlistedContainerStyle = '',
  notWishlistedContainerStyle = '',
  onWishlistChange
}: WishlistButtonProps) => {
  const { eventTracking } = useCookie()
  const { formatMessage } = useIntl()
  const navigate = useNavigation()
  const location = useLocation()
  const wishlist = useWishlist()
  const [wishlistLoading, setWishlistLoading] = useState<string[]>([])
  const [isWishlistInitialized, setIsWishlistInitialized] = useState(
    wishlist.isInitialized
  )

  const productId = product?.productId || product?.id
  const isInWishlist = !!wishlist.findItemByProductId(productId)
  const isMounted = useRef(false)

  const messages = defineMessages({
    add: {
      id: 'msp.account.wishlist.product_added_successfully',
      defaultMessage: 'The product was added to your wishlist.'
    },
    remove: {
      id: 'msp.account.wishlist.product_removed_successfully',
      defaultMessage: 'The product was removed from your wishlist.'
    },
    removeError: {
      id: 'msp.account.wishlist.product_removed_error',
      defaultMessage:
        'An error occurred while removing an item from your wishlist. Please try again later.'
    },
    addError: {
      id: 'msp.account.wishlist.product_added_error',
      defaultMessage:
        'An error occurred while adding an item from your wishlist. Please try again later.'
    },
    ariaLabel: {
      id: 'msp.wishlist.btn.ariaLabel',
      defaultMessage: 'Add to wishlist'
    }
  })

  useEffect(() => {
    isMounted.current = true
    setIsWishlistInitialized(wishlist.isInitialized)

    return () => {
      isMounted.current = false
    }
  }, [wishlist.isInitialized])

  const addItemToWishlist = async (productId: string) => {
    if (!isWishlistInitialized) {
      navigate(
        `/login${
          location?.pathname
            ? `?redirectUrl=${location.pathname}${
                location?.search ? location.search : ''
              }`
            : ''
        }`
      )
    } else {
      try {
        setWishlistLoading([...wishlistLoading, productId])
        await wishlist
          .createListItem({
            id: productId,
            quantity: 1
          })
          .then(() => {
            onWishlistChange &&
              onWishlistChange({
                type: 'success',
                message: formatMessage(messages.add)
              })

            eventTracking('add_to_wishlist', {
              ecommerce: {
                items: [getEventItemFromProduct(product)]
              }
            })
          })
      } catch {
        onWishlistChange &&
          onWishlistChange({
            type: 'error',
            message: formatMessage(messages.addError)
          })
      } finally {
        if (isMounted.current) {
          setWishlistLoading(wishlistLoading.filter((id) => id !== productId))
        }
      }
    }
  }

  const removeItemFromWishlist = async (productId: string) => {
    try {
      setWishlistLoading([...wishlistLoading, productId])
      await wishlist.removeListItemByProductId(productId).then(() => {
        onWishlistChange &&
          onWishlistChange({
            type: 'success',
            message: formatMessage(messages.remove)
          })

        eventTracking('remove_from_wishlist', {
          ecommerce: {
            items: [getEventItemFromProduct(product)]
          }
        })
      })
    } catch {
      onWishlistChange &&
        onWishlistChange({
          type: 'error',
          message: formatMessage(messages.removeError)
        })
    } finally {
      if (isMounted.current) {
        setWishlistLoading(wishlistLoading.filter((id) => id !== productId))
      }
    }
  }

  return isWishlistPage ? (
    <IconButton
      className='h-12 w-12 justify-center'
      iconName='Close'
      width='20px'
      height='20px'
      onClick={
        isInWishlist ? () => removeItemFromWishlist(productId) : () => {}
      }
    />
  ) : productId ? (
    <Button
      type='tertiary'
      variant='default'
      iconName={isInWishlist ? 'Wishlist' : 'WishlistAdd'}
      className={
        isInWishlist ? wishlistedContainerStyle : notWishlistedContainerStyle
      }
      isLoading={wishlistLoading.length > 0}
      isDisabled={isDisabled}
      loadingContent={<Spinner />}
      deactivateTransition={isInWishlist}
      onClick={isInWishlist ? () => {} : () => addItemToWishlist(productId)}
      ariaLabel={formatMessage(messages.ariaLabel)}
    />
  ) : null
}

export default WishlistButton
